import {
  all,
  type AllEffect,
  debounce,
  type ForkEffect,
  takeEvery,
} from 'redux-saga/effects';
import { Manager, type Socket } from 'socket.io-client';

import {
  accountDelete,
  addressCreate,
  addressDelete,
  addressesList,
  addressPatch,
  couponsList,
  dataExport,
  login,
  loginLinkChallengeRequest,
  loginLinkRequest,
  logout,
  orderCancel,
  orderGet,
  orderPaymentRetry,
  ordersList,
  passwordRemove,
  passwordSet,
  profileGet,
  referralCodeGet,
  settingsUpdate,
} from './reducers/account/reducer';
import {
  addProduct,
  removeProduct,
  validateDiscountCodes,
  validateVies,
} from './reducers/basket/reducer';
import { emailValidatorVerify } from './reducers/email-validator/reducer';
import { sendWithTimeout } from './reducers/email-validator/send-with-timeout';
import { eventSend } from './reducers/event/reducer';
import {
  giftRegistryCreate,
  giftRegistryExtendDeadline,
  giftRegistryItemAdd,
  giftRegistryItemConfirm,
  giftRegistryItemQuote,
  giftRegistryItemReject,
  giftRegistryItemRemove,
  giftRegistryItemsList,
  giftRegistryList,
  giftRegistryUpdateVisibility,
} from './reducers/gift-registry/reducer';
import { giftRegistryContribute } from './reducers/gift-registry-public/reducer';
import { marketingPreferenceUpdate } from './reducers/marketing-preference/reducer';
import { sendMessage } from './reducers/messenger/reducer';
import { create, paymentRetry } from './reducers/order/reducer';
import { load as loadProduct } from './reducers/product/reducer';
import { watchdogRegister } from './reducers/product-watchdog/reducer';
import {
  load as loadProducts,
  loadAvailableValues,
  loadByBrand,
  loadByProperties,
  loadCategoryAvailability,
  loadCategoryProperties,
  loadFeatured,
  loadFilteredTotal,
} from './reducers/products/reducer';
import {
  getProducts,
  putReview,
  submitReview,
} from './reducers/review/reducer';
import { loadAllReviews } from './reducers/reviews/reducer';
import { query, queryPage } from './reducers/search/reducer';
import {
  send,
  sendPayment,
  sendWithoutResponse,
} from './reducers/send-request';
import {
  changeCurrencyType,
  changeLanguageType,
} from './reducers/user/reducer';
import { withdrawalSubmit } from './reducers/withdrawal/reducer';

const manager = new Manager(window.location.origin, {
  transports: ['websocket'],
});
const socket = manager.socket('/');

export const getSocket = (): Socket => socket;

export const watch = function* (): Generator<ForkEffect<never>, void, string> {
  yield takeEvery(addressCreate.type, send, socket);
  yield takeEvery(addressDelete.type, send, socket);
  yield takeEvery(addressesList.type, send, socket);
  yield takeEvery(addressPatch.type, send, socket);
  yield takeEvery(login.type, send, socket);
  yield takeEvery(loginLinkChallengeRequest.type, send, socket);
  yield takeEvery(loginLinkRequest.type, send, socket);
  yield takeEvery(logout.type, send, socket);
  yield takeEvery(orderCancel.type, send, socket);
  yield takeEvery(orderGet.type, send, socket);
  yield takeEvery(orderPaymentRetry.type, sendPayment, socket);
  yield takeEvery(ordersList.type, send, socket);
  yield takeEvery(passwordRemove.type, send, socket);
  yield takeEvery(passwordSet.type, send, socket);
  yield takeEvery(profileGet.type, send, socket);
  yield takeEvery(addProduct.type, send, socket);
  yield takeEvery(create.type, sendPayment, socket);
  yield takeEvery(paymentRetry.type, sendPayment, socket);
  yield takeEvery(emailValidatorVerify.type, sendWithTimeout, socket);
  yield takeEvery(eventSend.type, sendWithoutResponse, socket);
  yield takeEvery(getProducts.type, send, socket);
  yield takeEvery(giftRegistryContribute.type, sendPayment, socket);
  yield takeEvery(giftRegistryCreate.type, send, socket);
  yield takeEvery(giftRegistryExtendDeadline.type, send, socket);
  yield takeEvery(giftRegistryItemAdd.type, send, socket);
  yield takeEvery(giftRegistryItemRemove.type, send, socket);
  yield takeEvery(giftRegistryItemsList.type, send, socket);
  yield takeEvery(giftRegistryItemConfirm.type, sendPayment, socket);
  yield takeEvery(giftRegistryItemQuote.type, send, socket);
  yield takeEvery(giftRegistryItemReject.type, send, socket);
  yield takeEvery(giftRegistryList.type, send, socket);
  yield takeEvery(giftRegistryUpdateVisibility.type, send, socket);
  yield takeEvery(changeCurrencyType.type, sendWithoutResponse, socket);
  yield takeEvery(changeLanguageType.type, sendWithoutResponse, socket);
  yield takeEvery(loadAllReviews.type, send, socket);
  yield takeEvery(loadByBrand.type, send, socket);
  yield takeEvery(loadByProperties.type, send, socket);
  yield takeEvery(loadCategoryProperties.type, send, socket);
  yield takeEvery(loadAvailableValues.type, send, socket);
  yield takeEvery(loadCategoryAvailability.type, send, socket);
  yield takeEvery(loadFilteredTotal.type, send, socket);
  yield takeEvery(loadFeatured.type, send, socket);
  yield takeEvery(loadProduct.type, send, socket);
  yield takeEvery(watchdogRegister.type, send, socket);
  yield takeEvery(loadProducts.type, send, socket);
  yield takeEvery(marketingPreferenceUpdate.type, send, socket);
  yield takeEvery(putReview.type, send, socket);
  yield takeEvery(queryPage.type, send, socket);
  // Debounced: one search request per typing pause instead of one per keystroke
  yield debounce(250, query.type, send, socket);
  yield takeEvery(removeProduct.type, send, socket);
  yield takeEvery(sendMessage.type, send, socket);
  yield takeEvery(submitReview.type, send, socket);
  yield takeEvery(validateDiscountCodes.type, send, socket);
  yield takeEvery(withdrawalSubmit.type, send, socket);
  yield takeEvery(validateVies.type, send, socket);
  yield takeEvery(couponsList.type, send, socket);
  yield takeEvery(referralCodeGet.type, send, socket);
  yield takeEvery(settingsUpdate.type, send, socket);
  yield takeEvery(dataExport.type, send, socket);
  yield takeEvery(accountDelete.type, send, socket);
};

export const mySaga = function* (): Generator<
  AllEffect<Generator<ForkEffect<never>, void, string>>,
  void,
  void
> {
  yield all([watch()]);
};
